import React, { useEffect, useState } from "react";
import {
  Card,
  Text,
  Grid,
  Select,
  Group,
  Button,
  Container,
  Title,
  SimpleGrid,
  Divider,
  Skeleton,
  ThemeIcon,
  Box,
  Space,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  IconFilter,
  IconCurrencyRupee,
  IconChartBar,
  IconCalendar,
  IconDownload,
  IconPackage,
  IconReceipt2,
} from "@tabler/icons-react";
import {
  getDocs,
  query,
  collection,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase/FirebaseCredential";
import { useAppSelector } from "../../../hooks/hooks";
import { RootState } from "../../../store/store";
import BenchBreadcrumbs from "../../UIComponent/Components/BenchBreadcrumbs";
import BenchTopHeader from "../../UIComponent/Components/BenchTopHeader";
import useBranchId from "../../../hooks/useBranchId";
import BenchHeading from "../../pages/UI/BenchHeading";
import { logError } from "../../ErrorLogService/ErrorLoggingService";
import dayjs from "dayjs";
import {
  startOfToday,
  endOfToday,
  startOfWeek,
  endOfWeek,
  subWeeks,
} from "date-fns";
import * as XLSX from "xlsx";

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const filterOptions = [
  { value: "today", label: "Today" },
  { value: "current_week", label: "Current Week" },
  { value: "previous_week", label: "Previous Week" },
  { value: "current_month", label: "Current Month" },
  { value: "previous_month", label: "Previous Month" },
  { value: "current_year", label: "Current Year" },
  { value: "previous_year", label: "Last Year" },
  { value: "specific_date", label: "Specific Date" },
  { value: "date_range", label: "Date Range" },
];

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D"];

export default function PackageSalesReport() {
  const companyId = useAppSelector((state: RootState) => state.session.company_id);
  const { branchesList } = useAppSelector((state) => state.branchList);

  const [selectedFilter, setSelectedFilter] = useState<string | null>("current_month");
  const { branchId, setBranchId } = useBranchId(branchesList);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [filterDate, setFilterDate] = useState<Date | null>(null);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalPackages: 0,
    totalTransactions: 0,
    avgPackageValue: 0,
  });
  const [packagePerformance, setPackagePerformance] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  useEffect(() => {
    if (companyId) {
      if (
        (selectedFilter === "specific_date" && !filterDate) ||
        (selectedFilter === "date_range" && (!fromDate || !toDate))
      ) {
        return;
      }
      fetchPackageSalesReport();
    }
  }, [companyId, branchId, selectedFilter, fromDate, toDate, filterDate]);

  const fetchPackageSalesReport = async () => {
    if (!companyId) return;
    setLoading(true);

    try {
      const isMonthlyOrYearly = [
        "current_month",
        "previous_month",
        "current_year",
        "previous_year",
      ].includes(selectedFilter || "");

      if (isMonthlyOrYearly) {
        await fetchFromPackageSummaries();
      } else {
        await fetchFromPaymentHistory();
      }
    } catch (error) {
      logError(error, "fetchPackageSalesReport");
      console.error("Error fetching package sales data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFromPackageSummaries = async () => {
    const summariesRef = collection(db, "companyprofile", companyId, "packagesummaries");
    let baseQuery = query(summariesRef);

    if (branchId && branchId !== "all_branches") {
      baseQuery = query(baseQuery, where("branch_id", "==", branchId));
    }

    const now = new Date();
    let years: number[] = [];
    let months: number[] = [];

    switch (selectedFilter) {
      case "current_month":
        years = [now.getFullYear()];
        months = [now.getMonth() + 1];
        break;
      case "previous_month": {
        const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
        const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        years = [prevYear];
        months = [prevMonth];
        break;
      }
      case "current_year":
        years = [now.getFullYear()];
        break;
      case "previous_year":
        years = [now.getFullYear() - 1];
        break;
    }

    if (years.length > 0) {
      baseQuery = query(baseQuery, where("year", "in", years));
    }

    const querySnapshot = await getDocs(baseQuery);
    let summaries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));

    if (months.length > 0) {
      summaries = summaries.filter((s: any) => months.includes(s.month));
    }

    processSalesData(summaries, true);
  };


  const fetchFromPaymentHistory = async () => {
    let baseQuery = query(collection(db, "paymenthistory"));
    baseQuery = query(baseQuery, where("company_id", "==", companyId));
    // We intentionally do not filter by type=="income" here because direct package payments often lack the 'type' field.

    if (branchId && branchId !== "all_branches") {
      baseQuery = query(baseQuery, where("branch_id", "==", branchId));
    }

    const now = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (selectedFilter) {
      case "today":
        startDate = startOfToday();
        endDate = endOfToday();
        break;
      case "current_week":
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case "previous_week":
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case "current_month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case "previous_month":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      case "current_year":
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case "previous_year":
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        break;
      case "specific_date":
        if (filterDate) {
          startDate = new Date(filterDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(filterDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      case "date_range":
        if (fromDate && toDate) {
          startDate = new Date(fromDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(toDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
    }

    if (startDate && endDate) {
      baseQuery = query(
        baseQuery,
        where("received_date", ">=", startDate),
        where("received_date", "<=", endDate)
      );
    }

    const querySnapshot = await getDocs(baseQuery);
    const records = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));

    processSalesData(records, false);
  };

  const processSalesData = (dataArray: any[], isSummary: boolean = false) => {
    const packageStats: Record<string, any> = {};
    const trendStats: Record<string, any> = {};
    let totalRev = 0;
    let totalQty = 0;
    let totalTransactionCount = 0;

    dataArray.forEach((item) => {
      const branchName = branchesList.find((b: any) => b.id === (item.branch_id || branchId))?.name || "Unknown Branch";
      
      if (isSummary) {
        totalRev += Number(item.total_amount) || 0;
        totalTransactionCount += Number(item.transaction_count) || 0;

        const dateKey = `${item.year}-${String(item.month).padStart(2, '0')}`;
        if (!trendStats[dateKey]) {
          trendStats[dateKey] = {
            date: new Date(item.year, item.month - 1, 1),
            label: dayjs(new Date(item.year, item.month - 1, 1)).format("MMM YYYY"),
            revenue: 0,
            items: 0,
          };
        }
        trendStats[dateKey].revenue += Number(item.total_amount) || 0;

        if (item.packages && Array.isArray(item.packages)) {
          item.packages.forEach((pkg: any) => {
            const pid = pkg.package_id || "unknown";
            const bid = item.branch_id || "unknown";
            const key = `${pid}_${bid}`;

            if (!packageStats[key]) {
              packageStats[key] = {
                name: pkg.package_name || "Unknown Package",
                branchName: branchName,
                qty: 0,
                revenue: 0,
              };
            }
            packageStats[key].qty += Number(pkg.package_count) || 1;
            packageStats[key].revenue += Number(pkg.amount) || 0;
            totalQty += Number(pkg.package_count) || 1;
          });
        }
      } else {
        // Raw logic
        const isPackage = !!(item.current_pkg_name || item.standard_package_name || item.package_id || item.payment_for?.toLowerCase().includes('package') || item.payment_for?.toLowerCase().includes('subscription'));
        
        if (item.invoice_id && !isPackage) {
           return; 
        }

        const status = item.current_pkg_transaction_status || item.status || "Success";
        if (status !== "Success" && status.toLowerCase() !== "paid") {
           return;
        }

        if (!isPackage && item.invoice_id) return;

        const amount = Number(item.amount) || Number(item.current_pkg_price) || 0;
        
        totalRev += amount;
        totalTransactionCount += 1;

        let dateObj: Date;
        if (item.received_date?.toDate) {
          dateObj = item.received_date.toDate();
        } else if (typeof item.received_date === 'number') {
          dateObj = new Date(item.received_date * 1000);
        } else if (item.received_date?.seconds) {
          dateObj = new Date(item.received_date.seconds * 1000);
        } else {
          dateObj = new Date(item.received_date || item.payment_date || Date.now());
        }

        const dateKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, '0')}`;

        if (!trendStats[dateKey]) {
          trendStats[dateKey] = { date: dateObj, label: dayjs(dateObj).format("DD MMM YYYY"), revenue: 0, items: 0 };
        }
        trendStats[dateKey].revenue += amount;

        const pkgName = item.current_pkg_name || item.standard_package_name || item.package_name || "Unknown Package";
        const pid = item.current_pkg_id || item.package_id || "unknown";
        const bid = item.branch_id || "unknown";
        const key = `${pid}_${bid}`;

        if (!packageStats[key]) {
          packageStats[key] = {
            name: pkgName,
            branchName: branchName,
            qty: 0,
            revenue: 0,
          };
        }
        packageStats[key].qty += 1;
        packageStats[key].revenue += amount;
        totalQty += 1;
      }
    });

    const perfArray = Object.values(packageStats).sort((a: any, b: any) => b.revenue - a.revenue);
    const trendArray = Object.values(trendStats).sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

    const finalTrendArray = trendArray.map((item: any) => ({
      date: item.label,
      revenue: item.revenue,
      items: item.items
    }));

    setSummary({
      totalRevenue: totalRev,
      totalPackages: totalQty,
      totalTransactions: totalTransactionCount,
      avgPackageValue: totalTransactionCount > 0 ? totalRev / totalTransactionCount : 0,
    });
    setPackagePerformance(perfArray);
    setTrendData(finalTrendArray);
  };

  const handleDownloadExcel = () => {
    const dataToExport = packagePerformance.map((p) => ({
      "Package Name": p.name,
      "Branch Name": p.branchName,
      "Packages Sold": p.qty,
      "Total Revenue": `₹${p.revenue.toFixed(2)}`,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Package Sales");
    XLSX.writeFile(workbook, `Package_Sales_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const breadcrumbLinks = [{ label: "Package Sales Report" }];

  return (
    <Container size="lg">
       <Box
            sx={(theme) => ({
              position: "sticky",
              top: 110, // Avoid overlapping the fixed navbar
              zIndex: 100,
              backgroundColor: "#FAFAFA",
              paddingTop: theme.spacing.sm,
              marginTop: `-${theme.spacing.sm}`,
            })}
          >
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <BenchBreadcrumbs links={breadcrumbLinks} />
        <Group position="right">
          <Button
            leftIcon={<IconDownload size={16} />}
            variant="outline"
            onClick={handleDownloadExcel}
            disabled={packagePerformance.length === 0}
          >
            Export Report
          </Button>
        </Group>
      </SimpleGrid>

      <Divider my="sm" />
</Box>
      <BenchTopHeader
        icon={<IconPackage />}
        title="Package Sales Report"
        description="Detailed insights into package performance, revenue, and sales trends."
      />

      {/* FILTERS */}
      <Card p="lg" shadow="md" radius="lg" mb="xl" withBorder my="xl">
        <Grid align="flex-end">
          <Grid.Col xs={12} sm={6} md={3}>
            <Select
              label="Branch"
              value={branchId}
              onChange={setBranchId}
              data={[
                ...branchesList.map((b: any) => ({ value: b.id, label: b.name })),
              ]}
              icon={<IconFilter size={16} />}
              withinPortal
            />
          </Grid.Col>
          <Grid.Col xs={12} sm={6} md={3}>
            <Select
              label="Filter By"
              value={selectedFilter}
              onChange={setSelectedFilter}
              data={filterOptions}
              placeholder="Select period"
              withinPortal
            />
          </Grid.Col>
          {selectedFilter === "specific_date" && (
            <Grid.Col xs={12} sm={6} md={3}>
              <DatePickerInput
                label="Select Date"
                value={filterDate}
                onChange={setFilterDate}
                icon={<IconCalendar size={16} />}
                valueFormat="DD MMM YYYY"
                popoverProps={{ withinPortal: true }}
              />
            </Grid.Col>
          )}
          {selectedFilter === "date_range" && (
            <Grid.Col xs={12} sm={6} md={3} sx={{ zIndex: 10 }}>
              <DatePickerInput
                label="From"
                value={fromDate}
                onChange={setFromDate}
                icon={<IconCalendar size={16} />}
                valueFormat="DD MMM YYYY"
                popoverProps={{ withinPortal: true }}
              />
            </Grid.Col>
          )}
          {selectedFilter === "date_range" && (
            <Grid.Col xs={12} sm={6} md={3} sx={{ zIndex: 11 }}>
              <DatePickerInput
                label="To"
                value={toDate}
                onChange={setToDate}
                icon={<IconCalendar size={16} />}
                valueFormat="DD MMM YYYY"
                popoverProps={{ withinPortal: true }}
              />
            </Grid.Col>
          )}

        </Grid>
      </Card>

      {/* SUMMARY CARDS */}
      <SimpleGrid cols={4} breakpoints={[{ maxWidth: "md", cols: 2 }, { maxWidth: "xs", cols: 1 }]} mb="xl">
        <SummaryCard
          title="Total Revenue"
          value={`₹${summary.totalRevenue.toLocaleString()}`}
          icon={<IconCurrencyRupee size={28} />}
          color="blue"
          loading={loading}
        />
        <SummaryCard
          title="Packages Sold"
          value={summary.totalPackages.toLocaleString()}
          icon={<IconPackage size={28} />}
          color="teal"
          loading={loading}
        />
        <SummaryCard
          title="Total Transactions"
          value={summary.totalTransactions.toLocaleString()}
          icon={<IconReceipt2 size={28} />}
          color="grape"
          loading={loading}
        />
        <SummaryCard
          title="Avg Package Value"
          value={`₹${summary.avgPackageValue.toFixed(2)}`}
          icon={<IconChartBar size={28} />}
          color="orange"
          loading={loading}
        />
      </SimpleGrid>

      {/* CHARTS */}
      <Grid gutter="xl" mb="xl">
        <Grid.Col md={7}>
          <Card p="lg" withBorder radius="md" shadow="sm">
            <BenchHeading title="Sales Trend" subheading="Revenue over time" order={4} fw={600} fz="h1" />
            <Space h="md" />
            {loading ? <Skeleton height={300} radius="md" /> : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevPkg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#20c997" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#20c997" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" interval="preserveStartEnd" />
                  <YAxis tickFormatter={(val) => `₹${val}`} />
                  <Tooltip formatter={(val) => `₹${Number(val).toLocaleString()}`} />
                  <Area type="monotone" dataKey="revenue" stroke="#20c997" fillOpacity={1} fill="url(#colorRevPkg)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col md={5}>
          <Card p="lg" withBorder radius="md" shadow="sm">
            <BenchHeading title="Top Packages" subheading="By revenue contribution" order={4} fw={600} fz="h1" />
            <Space h="md" />
            {loading ? <Skeleton height={300} radius="md" /> : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={packagePerformance.slice(0, 5)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    dataKey="revenue"
                    nameKey="name"
                    label={renderCustomizedLabel}
                  >
                    {packagePerformance.slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => `₹${Number(val).toLocaleString()}`} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      <Space h="xl" />
    </Container>
  );
}

function SummaryCard({ title, value, icon, color, loading }: any) {
  return (
    <Card shadow="md" p="lg" radius="md" withBorder>
      <Group position="apart" align="flex-start" noWrap>
        <div>
          <Text size="xs" color="dimmed" weight={700} transform="uppercase">
            {title}
          </Text>
          {loading ? <Skeleton height={30} width={100} mt="xs" /> : (
            <Title order={3} color={color} mt="xs">
              {value}
            </Title>
          )}
        </div>
        <ThemeIcon size={48} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}
